import { ChangeEvent, FC, ReactElement, useCallback } from "react"
import { FieldArray, FormikProps, useFormikContext } from "formik" // eslint-disable-line

import { useTranslation } from "react-i18next"

import { TenantColorsData } from "../../../types/api"
import { deriveOriginalsDefaults } from "../../../util/originalsTheme"
import { InputColor } from "../../inputs/InputColor"
import { TitledCheckbox } from "../../inputs/TitledCheckbox"
import { ToolTip } from "../components/ToolTip"

/**
 * "Originals games colors" group of the tenant colour editor.
 *
 * The toggle mirrors storage: on iff a block is stored. Enabling pre-fills
 * every field from the live derivation so the games do not change until a
 * field is edited — Save then persists all of them as explicit overrides.
 * Disabling sets the block to `undefined`; the submit path drops it.
 */

// Labels and tooltips live under cms.originalsColors.fields.<name> in the locale files.
const FIELD_NAMES: (keyof NonNullable<TenantColorsData["originals"]>)[] = [
  "surface",
  "accent",
  "text",
  "textMuted",
  "border",
  "card",
  "buttonText"
]

export const OriginalsColorFields: FC = (): ReactElement => {
  const { t } = useTranslation()
  const { values, setFieldValue } = useFormikContext<TenantColorsData>()
  // Any stored block counts as on, even with blank fields: the operator must
  // see exactly what is persisted. Blanks are normalised on submit
  // (withCleanOriginals), not hidden here.
  const enabled = Boolean(values.originals)

  const toggle = useCallback(
    (next: boolean) => {
      setFieldValue("originals", next ? deriveOriginalsDefaults(values) : undefined)
    },
    [values, setFieldValue]
  )

  return (
    <div className="my-10">
      <h4>{t("cms.originalsColors.title")}</h4>
      <div className="my-3 relative group">
        <ToolTip text={t("cms.originalsColors.toggleTooltip")} />
        <TitledCheckbox
          id="originalsOverrideEnabled"
          title={t("cms.originalsColors.toggle")}
          checked={enabled}
          onChange={(event: ChangeEvent<HTMLInputElement>) => toggle(event.target.checked)}
        />
      </div>

      {!enabled && (
        <p className="text-sm opacity-70 my-2">{t("cms.originalsColors.hint")}</p>
      )}

      {enabled && (
        <>
          {FIELD_NAMES.map((name) => (
            <div key={name} className="my-2 relative group">
              <ToolTip text={t(`cms.originalsColors.fields.${name}.tooltip`)} />
              <InputColor
                name={`originals.${name}`}
                value={(values.originals?.[name] as string) ?? ""}
                onChange={(value: string) => setFieldValue(`originals.${name}`, value)}
                title={t(`cms.originalsColors.fields.${name}.title`)}
              />
            </div>
          ))}

          {/* Stop count is fixed to the pre-filled list (the site's main gradient length); add/remove is
              deliberately not exposed — a flat button is the same colour in every stop. The package
              needs >= 2 stops; shorter lists are dropped on save. */}
          <FieldArray
            name="originals.gradient"
            render={({ form }) => {
              const stops = (form as FormikProps<TenantColorsData>).values.originals?.gradient ?? []
              return (
                <div className="mt-6 relative group">
                  <ToolTip text={t("cms.originalsColors.gradient.tooltip")} />
                  <h4>{t("cms.originalsColors.gradient.title")}</h4>
                  {stops.map((_, index) => (
                    <div key={index} className="flex items-end">
                      <span className="mr-3">{index + 1}</span>
                      <InputColor
                        name={`originals.gradient.${index}`}
                        value={stops[index]}
                        onChange={(value: string) => form.setFieldValue(`originals.gradient.${index}`, value)}
                      />
                    </div>
                  ))}
                </div>
              )
            }}
          />
        </>
      )}
    </div>
  )
}
